import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { addToCart, generateUserId, getCart, getServiceById } from '../firebase/config';
import { getDatabase, ref, get } from 'firebase/database';
import './Pages.css';

// Import Material-UI Icons
import WhatshotIcon from '@mui/icons-material/Whatshot';
import PublicIcon from '@mui/icons-material/Public';
import LanguageIcon from '@mui/icons-material/Language';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonIcon from '@mui/icons-material/Person';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import ComputerIcon from '@mui/icons-material/Computer';
import VerifiedIcon from '@mui/icons-material/Verified';
import SchoolIcon from '@mui/icons-material/School';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import QuizIcon from '@mui/icons-material/Quiz';
import PsychologyIcon from '@mui/icons-material/Psychology';
import DescriptionIcon from '@mui/icons-material/Description';
import ChecklistIcon from '@mui/icons-material/Checklist';
import ScheduleIcon from '@mui/icons-material/Schedule';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import SearchIcon from '@mui/icons-material/Search';
import GridViewIcon from '@mui/icons-material/GridView';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import BadgeIcon from '@mui/icons-material/Badge';
import StarIcon from '@mui/icons-material/Star';
import CodeIcon from '@mui/icons-material/Code';
import ReceiptIcon from '@mui/icons-material/Receipt';
import FolderIcon from '@mui/icons-material/Folder';

// Map icons to their components
const iconMap = {
  'whatshot': WhatshotIcon,
  'public': PublicIcon,
  'language': LanguageIcon,
  'groups': GroupsIcon,
  'person': PersonIcon,
  'menu_book': MenuBookIcon,
  'linkedin': LinkedInIcon,
  'computer': ComputerIcon,
  'verified': VerifiedIcon,
  'school': SchoolIcon,
  'fact_check': FactCheckIcon,
  'quiz': QuizIcon,
  'psychology': PsychologyIcon,
  'description': DescriptionIcon,
  'checklist': ChecklistIcon,
  'schedule': ScheduleIcon,
  'rocket_launch': RocketLaunchIcon,
  'search': SearchIcon,
  'grid_view': GridViewIcon,
  'how_to_reg': HowToRegIcon,
  'badge': BadgeIcon,
  'star': StarIcon,
  'code': CodeIcon,
  'receipt': ReceiptIcon,
};

// Fallback icons for categories
const categoryFallbackIcons = {
  'Career Blaze': WhatshotIcon,
  'Improve Profile': PersonIcon,
  'Profile Verification': VerifiedIcon,
  'Career Assistance': PsychologyIcon,
  'Maximize Reach': RocketLaunchIcon,
  'Profile ID': BadgeIcon,
};

const getServiceIconComponent = (iconName, category) => {
  if (iconName && iconMap[iconName]) {
    return iconMap[iconName];
  }
  if (category && categoryFallbackIcons[category]) {
    return categoryFallbackIcons[category];
  }
  return FolderIcon;
};

const getCategoryColor = (category) => {
  const colorMap = {
    'Career Blaze': '#FF6B6B',
    'Improve Profile': '#4ECDC4',
    'Profile Verification': '#45B7D1',
    'Career Assistance': '#96CEB4',
    'Maximize Reach': '#FFEAA7',
    'Profile ID': '#DDA0DD'
  };
  return colorMap[category] || '#65d2bc';
};

const ServicePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [added, setAdded] = useState(false);
  const [serviceData, setServiceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Custom service states - User fills these
  const [customFields, setCustomFields] = useState({
    fullName: '',
    termsAccepted: false
  });
  const [showCustomFields, setShowCustomFields] = useState(false);
  const [customFieldErrors, setCustomFieldErrors] = useState({});
  const [isCustomService, setIsCustomService] = useState(false);

  useEffect(() => {
    let isMounted = true;
    
    console.log('🔍 ServicePage mounted');
    console.log('🔍 ID from useParams:', id);

    const fetchService = async () => {
      if (!id) {
        console.error('❌ No service ID provided');
        if (isMounted) {
          setError('No service ID provided');
          setLoading(false);
        }
        return;
      }

      try {
        if (isMounted) {
          setLoading(true);
          setError(null);
        }
        
        console.log('📡 Fetching service with ID:', id);
        
        // First try to get from custom services
        const db = getDatabase();
        const customSnapshot = await get(ref(db, 'customService/' + id));
        
        let serviceResult = null;
        
        if (customSnapshot.exists()) {
          // It's a custom service
          const customData = customSnapshot.val();
          serviceResult = {
            success: true,
            data: {
              id: id,
              ...customData,
              isCustom: true,
              type: 'custom'
            }
          };
          console.log('✅ Custom service found:', customData);
        } else {
          // Try regular services
          serviceResult = await getServiceById(id);
        }
        
        if (isMounted) {
          if (serviceResult && serviceResult.success && serviceResult.data) {
            console.log('✅ Service data loaded successfully:', serviceResult.data);
            const data = { id: id, ...serviceResult.data };
            setServiceData(data);
            const isCustom = data.isCustom || data.type === 'custom' || false;
            setIsCustomService(isCustom);
            
            // For custom services, show the custom fields form
            if (isCustom) {
              setShowCustomFields(true);
            } else {
              setShowCustomFields(false);
            }
            
            setError(null);
          } else {
            console.error('❌ Service not found');
            setError('Service not found');
            setServiceData(null);
          }
        }
      } catch (error) {
        console.error('❌ Exception in fetchService:', error);
        if (isMounted) {
          setError('Failed to load service. Please try again.');
          setServiceData(null);
        }
      } finally {
        if (isMounted) {
          console.log('🏁 Fetch completed, setting loading to false');
          setLoading(false);
        }
      }
    };

    fetchService();

    return () => {
      console.log('🧹 Cleaning up ServicePage');
      isMounted = false;
    };
  }, [id]);

  const addToCartDirect = async () => {
    if (!serviceData) {
      console.error('❌ Cannot add to cart: No service data');
      return;
    }
    
    console.log('🛒 Adding to cart:', serviceData);
    
    const currentUser = localStorage.getItem('currentUser');
    let userId = null;
    let isLoggedIn = false;

    if (currentUser) {
      try {
        const user = JSON.parse(currentUser);
        if (user.isLoggedIn && user.userId) {
          userId = user.userId;
          isLoggedIn = true;
        }
      } catch (e) {
        console.error('Error parsing currentUser:', e);
      }
    }

    if (!isLoggedIn) {
      let guestId = localStorage.getItem('guestUserId');
      if (!guestId) {
        guestId = 'guest_' + generateUserId();
        localStorage.setItem('guestUserId', guestId);
      }
      userId = guestId;
    }

    console.log('👤 User ID:', userId);
    console.log('👤 Is logged in:', isLoggedIn);

    const item = {
      id: serviceData.id,
      name: serviceData.name,
      price: serviceData.price,
      gst: serviceData.gst,
      total: serviceData.total,
      image: serviceData.image || 'folder',
      category: serviceData.category,
      isCustom: isCustomService,
      customFields: customFields
    };

    try {
      let existingCart = [];
      
      if (isLoggedIn) {
        const cartResult = await getCart(userId);
        if (cartResult.success) {
          existingCart = cartResult.data || [];
        }
      } else {
        const guestCartData = localStorage.getItem(`cart_${userId}`);
        if (guestCartData) {
          try {
            existingCart = JSON.parse(guestCartData);
          } catch (e) {
            existingCart = [];
          }
        }
      }

      const existingItemIndex = existingCart.findIndex(i => i.id === item.id);
      
      if (existingItemIndex !== -1) {
        alert(`${serviceData.name} is already in your cart!`);
        setAdded(true);
        setTimeout(() => setAdded(false), 2000);
        return;
      }

      let result;
      if (isLoggedIn) {
        result = await addToCart(userId, item);
      } else {
        existingCart.push(item);
        localStorage.setItem(`cart_${userId}`, JSON.stringify(existingCart));
        result = { success: true, data: existingCart };
      }
      
      if (result.success) {
        setAdded(true);
        setTimeout(() => setAdded(false), 2000);
        localStorage.setItem('pendingCartItems', JSON.stringify(result.data || existingCart));
        
        // Store custom service data if applicable
        if (isCustomService) {
          localStorage.setItem('customServiceData', JSON.stringify({
            fullName: customFields.fullName,
            termsAccepted: customFields.termsAccepted,
            serviceId: serviceData.id,
            serviceName: serviceData.name
          }));
        }
        
        // Dispatch custom event to notify Header about cart update
        const cartUpdateEvent = new CustomEvent('cartUpdated', { 
          detail: { 
            userId: userId,
            isLoggedIn: isLoggedIn,
            cartData: result.data || existingCart 
          } 
        });
        window.dispatchEvent(cartUpdateEvent);
        
        setTimeout(() => {
          navigate('/cart');
        }, 500);
      } else {
        alert('Failed to add item to cart. Please try again.');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      alert('Error adding to cart. Please try again.');
    }
  };

  const handleAddToCart = async () => {
    if (!serviceData) return;
    
    // For custom service, show custom fields first
    if (isCustomService) {
      setShowCustomFields(true);
      return;
    }
    
    await addToCartDirect();
  };

  const handleCustomFieldSubmit = () => {
    const errors = {};
    
    if (!customFields.fullName.trim()) {
      errors.fullName = 'Please enter your full name';
    }
    if (!customFields.termsAccepted) {
      errors.terms = 'Please accept the terms and conditions';
    }
    
    if (Object.keys(errors).length > 0) {
      setCustomFieldErrors(errors);
      return;
    }
    
    // Proceed to cart
    addToCartDirect();
  };

  const handleNavigation = (path) => {
    navigate(path);
  };

  // Loading State
  if (loading) {
    return React.createElement(
      'main',
      { className: 'page-content' },
      React.createElement(
        'div',
        { className: 'container', style: { textAlign: 'center', padding: '60px 0' } },
        React.createElement(
          'div',
          { 
            className: 'spinner-border text-primary', 
            role: 'status',
            style: { width: '3rem', height: '3rem' }
          },
          React.createElement('span', { className: 'visually-hidden' }, 'Loading...')
        ),
        React.createElement('p', { style: { marginTop: '20px', color: '#64748b' } }, 'Loading service details...'),
        id && React.createElement('p', { style: { fontSize: '12px', color: '#94a3b8', marginTop: '8px' } }, `Service ID: ${id}`)
      )
    );
  }

  // Error State
  if (error || !serviceData) {
    return React.createElement(
      'main',
      { className: 'page-content' },
      React.createElement(
        'div',
        { className: 'container', style: { textAlign: 'center', padding: '60px 0' } },
        React.createElement('i', { className: 'fas fa-exclamation-circle', style: { fontSize: '48px', color: '#FF6B6B' } }),
        React.createElement('h2', { style: { marginTop: '20px', color: '#1e293b' } }, error || 'Service Not Found'),
        React.createElement('p', { style: { color: '#64748b', marginBottom: '20px' } },
          error ? 'Please try again or return to home.' : 'The service you are looking for does not exist.'
        ),
        id && React.createElement('p', { style: { fontSize: '12px', color: '#94a3b8', marginBottom: '20px' } }, `Service ID: ${id}`),
        React.createElement(
          'button',
          {
            className: 'btn-primary',
            onClick: () => handleNavigation('/'),
            style: {
              padding: '12px 24px',
              background: '#65d2bc',
              border: 'none',
              borderRadius: '8px',
              color: '#0f172a',
              fontSize: '16px',
              cursor: 'pointer'
            }
          },
          'Back to Home'
        )
      )
    );
  }

  const IconComponent = getServiceIconComponent(serviceData.image, serviceData.category);
  const categoryColor = getCategoryColor(serviceData.category);
  const isCustom = isCustomService || serviceData.isCustom || serviceData.type === 'custom';

  return React.createElement(
    'main',
    { className: 'page-content' },
    React.createElement(
      'div',
      { className: 'container' },
      // Breadcrumb
      React.createElement(
        'div',
        { className: 'breadcrumb' },
        React.createElement(
          'a',
          { 
            href: '#',
            onClick: (e) => { e.preventDefault(); handleNavigation('/'); },
            style: { color: '#65d2bc', textDecoration: 'none', cursor: 'pointer' }
          },
          React.createElement('i', { className: 'fas fa-home' }),
          ' Home'
        ),
        React.createElement('span', { className: 'separator' }, ' / '),
        React.createElement(
          'a',
          { 
            href: '#',
            onClick: (e) => { e.preventDefault(); handleNavigation('/services'); },
            style: { color: '#65d2bc', textDecoration: 'none', cursor: 'pointer' }
          },
          'Services'
        ),
        React.createElement('span', { className: 'separator' }, ' / '),
        React.createElement('span', { style: { color: isCustom ? '#8B5CF6' : categoryColor } }, serviceData.category),
        React.createElement('span', { className: 'separator' }, ' / '),
        React.createElement('span', null, serviceData.name),
        isCustom && React.createElement(
          'span',
          { 
            style: { 
              background: '#8B5CF6', 
              color: 'white', 
              padding: '2px 12px', 
              borderRadius: '12px',
              fontSize: '12px',
              marginLeft: '10px',
              fontWeight: '600'
            }
          },
          'Custom'
        )
      ),
      // Service Detail
      React.createElement(
        'div',
        { className: 'service-detail' },
        // Product Image - Full White Card with Icon
        React.createElement(
          'div',
          { 
            className: 'service-image',
            style: {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
              minHeight: '400px'
            }
          },
          React.createElement(
            'div',
            {
              style: {
                width: '100%',
                height: '100%',
                minHeight: '400px',
                background: '#ffffff',
                borderRadius: '16px',
                boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06)',
                border: isCustom ? '2px solid #8B5CF6' : '1px solid #e2e8f0',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '40px',
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'hidden'
              },
              className: 'service-icon-card',
              onMouseEnter: (e) => {
                e.currentTarget.style.transform = 'scale(1.01)';
                e.currentTarget.style.boxShadow = '0 8px 30px rgba(0, 0, 0, 0.1)';
              },
              onMouseLeave: (e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = '0 4px 24px rgba(0, 0, 0, 0.06)';
              }
            },
            // Decorative background element 1
            React.createElement(
              'div',
              {
                style: {
                  position: 'absolute',
                  top: '-30%',
                  right: '-20%',
                  width: '400px',
                  height: '400px',
                  background: `radial-gradient(circle, ${isCustom ? '#8B5CF6' : categoryColor}08, transparent)`,
                  borderRadius: '50%',
                  pointerEvents: 'none'
                }
              }
            ),
            // Decorative background element 2
            React.createElement(
              'div',
              {
                style: {
                  position: 'absolute',
                  bottom: '-20%',
                  left: '-20%',
                  width: '300px',
                  height: '300px',
                  background: `radial-gradient(circle, ${isCustom ? '#8B5CF6' : categoryColor}06, transparent)`,
                  borderRadius: '50%',
                  pointerEvents: 'none'
                }
              }
            ),
            // Large Icon - Centered
            React.createElement(IconComponent, { 
              style: { 
                fontSize: '140px', 
                color: isCustom ? '#8B5CF6' : categoryColor,
                position: 'relative',
                zIndex: 1,
                filter: 'drop-shadow(0 4px 20px rgba(0,0,0,0.04))',
                marginBottom: '16px',
                width: '140px',
                height: '140px'
              },
              className: 'service-icon'
            }),
            // Category label below icon
            React.createElement(
              'div',
              { 
                className: 'service-icon-label',
                style: { 
                  position: 'relative',
                  zIndex: 1,
                  marginTop: '8px',
                  fontSize: '15px',
                  fontWeight: '600',
                  color: isCustom ? '#8B5CF6' : '#64748b',
                  textTransform: 'uppercase',
                  letterSpacing: '2px'
                }
              },
              serviceData.category
            ),
            // Service name below category
            React.createElement(
              'div',
              {
                style: {
                  position: 'relative',
                  zIndex: 1,
                  marginTop: '4px',
                  fontSize: '18px',
                  fontWeight: '700',
                  color: '#0f172a'
                }
              },
              serviceData.name
            ),
            // Custom badge
            isCustom && React.createElement(
              'div',
              {
                style: {
                  position: 'absolute',
                  top: '16px',
                  right: '16px',
                  background: '#8B5CF6',
                  color: 'white',
                  padding: '4px 14px',
                  borderRadius: '20px',
                  fontSize: '12px',
                  fontWeight: '600',
                  zIndex: 2
                }
              },
              '⭐ Custom Service'
            )
          )
        ),
        // Product Info
        React.createElement(
          'div',
          { className: 'service-info' },
          React.createElement('h1', { 
            className: 'service-title',
            style: { 
              fontSize: '32px',
              fontWeight: '800',
              color: '#0f172a',
              margin: '0',
              lineHeight: '1.2'
            }
          }, serviceData.name),
          React.createElement(
            'div',
            { 
              className: 'service-pricing',
              style: {
                background: '#f8fafc',
                padding: '20px 24px',
                borderRadius: '16px',
                border: isCustom ? '2px solid #8B5CF6' : '1px solid #e2e8f0'
              }
            },
            React.createElement(
              'div',
              { className: 'price-row', style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #e2e8f0' } },
              React.createElement('span', { className: 'price-label', style: { color: '#64748b', fontSize: '14px', fontWeight: '500' } }, 'Unit Price:'),
              React.createElement('span', { className: 'price-value', style: { color: '#1e293b', fontSize: '16px', fontWeight: '600' } }, `₹${serviceData.price ? serviceData.price.toLocaleString() : 0}`)
            ),
            React.createElement(
              'div',
              { className: 'price-row', style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #e2e8f0' } },
              React.createElement('span', { className: 'price-label', style: { color: '#64748b', fontSize: '14px', fontWeight: '500' } }, 'GST (18%):'),
              React.createElement('span', { className: 'price-value', style: { color: '#1e293b', fontSize: '16px', fontWeight: '600' } }, `₹${serviceData.gst ? serviceData.gst.toLocaleString() : 0}`)
            ),
            React.createElement(
              'div',
              { className: 'price-row total', style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0 0 0', borderTop: `2px solid ${isCustom ? '#8B5CF6' : categoryColor}` } },
              React.createElement('span', { className: 'price-label', style: { color: '#0f172a', fontSize: '16px', fontWeight: '700' } }, 'Total:'),
              React.createElement('span', { className: 'price-value total-price', style: { color: isCustom ? '#8B5CF6' : categoryColor, fontSize: '22px', fontWeight: '800' } }, `₹${serviceData.total ? serviceData.total.toLocaleString() : 0}`)
            )
          ),
          React.createElement(
            'div',
            { className: 'service-description' },
            React.createElement('h4', { style: { fontSize: '16px', fontWeight: '700', color: '#0f172a', marginBottom: '8px' } }, 'Service Description'),
            React.createElement('p', { style: { color: '#475569', lineHeight: '1.8', fontSize: '15px', margin: '0' } }, serviceData.description || 'No description available')
          ),
          // Features Section
serviceData.features && serviceData.features.length > 0 && React.createElement(
  'div',
  { 
    className: 'service-features',
    style: {
      marginTop: '8px',
      padding: '20px 24px',
      background: '#f8fafc',
      borderRadius: '16px',
      border: '1px solid #e2e8f0'
    }
  },
  React.createElement(
    'h4',
    { style: { fontSize: '16px', fontWeight: '700', color: '#0f172a', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' } },
    React.createElement('i', { className: 'fas fa-star', style: { color: isCustom ? '#8B5CF6' : categoryColor } }),
    'Key Features'
  ),
  React.createElement(
    'ul',
    { style: { listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '10px' } },
    serviceData.features.map(function(feature, index) {
      return React.createElement(
        'li',
        { 
          key: index,
          style: { 
            display: 'flex', 
            alignItems: 'flex-start', 
            gap: '10px',
            color: '#475569',
            fontSize: '15px',
            lineHeight: '1.5'
          }
        },
        React.createElement('i', { 
          className: 'fas fa-check-circle', 
          style: { 
            color: isCustom ? '#8B5CF6' : categoryColor, 
            marginTop: '3px',
            flexShrink: 0
          } 
        }),
        React.createElement('span', null, feature)
      );
    })
  )
),
          // Custom Fields for Custom Services - Full Name and Checkbox for Terms
          isCustom && showCustomFields && React.createElement(
            'div',
            { 
              className: 'custom-fields-container',
              style: {
                marginTop: '20px',
                padding: '24px',
                background: '#f8fafc',
                borderRadius: '16px',
                border: '2px solid #8B5CF6'
              }
            },
            React.createElement(
              'h4',
              { style: { marginBottom: '16px', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' } },
              React.createElement('i', { className: 'fas fa-pencil-alt', style: { color: '#8B5CF6' } }),
              'Additional Information Required'
            ),
            
            // Full Name - Required for all custom services
            React.createElement(
              'div',
              { className: 'form-group', style: { marginBottom: '16px' } },
              React.createElement('label', { style: { fontWeight: '600', display: 'block', marginBottom: '6px' } },
                'Full Name *'
              ),
              React.createElement('input', {
                type: 'text',
                className: 'form-control',
                placeholder: 'Enter your full name',
                value: customFields.fullName,
                onChange: (e) => {
                  setCustomFields({ ...customFields, fullName: e.target.value });
                  setCustomFieldErrors({ ...customFieldErrors, fullName: '' });
                },
                style: {
                  width: '100%',
                  padding: '12px',
                  border: `2px solid ${customFieldErrors.fullName ? '#dc2626' : '#e2e8f0'}`,
                  borderRadius: '8px',
                  fontSize: '16px'
                }
              }),
              customFieldErrors.fullName && React.createElement(
                'span',
                { style: { color: '#dc2626', fontSize: '13px', marginTop: '4px', display: 'block' } },
                customFieldErrors.fullName
              )
            ),
            
            // Terms Acceptance - Single Checkbox
            React.createElement(
              'div',
              { className: 'form-group', style: { marginBottom: '16px' } },
              React.createElement(
                'label',
                { 
                  className: 'terms-label',
                  style: { 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: '12px',
                    cursor: 'pointer',
                    padding: '14px',
                    background: customFieldErrors.terms ? '#fef2f2' : '#ffffff',
                    borderRadius: '8px',
                    border: customFieldErrors.terms ? '2px solid #dc2626' : '2px solid #e2e8f0',
                    transition: 'all 0.3s ease'
                  }
                },
                React.createElement('input', {
                  type: 'checkbox',
                  checked: customFields.termsAccepted,
                  onChange: (e) => {
                    setCustomFields({ ...customFields, termsAccepted: e.target.checked });
                    setCustomFieldErrors({ ...customFieldErrors, terms: '' });
                  },
                  style: { 
                    marginTop: '2px', 
                    width: '20px', 
                    height: '20px', 
                    cursor: 'pointer',
                    flexShrink: 0,
                    accentColor: '#8B5CF6'
                  }
                }),
                React.createElement(
                  'span',
                  { style: { fontSize: '14px', color: '#475569', lineHeight: '1.6' } },
                  serviceData.termsText || 'I hereby accept and agree to the terms and conditions.'
                )
              ),
              customFieldErrors.terms && React.createElement(
                'span',
                { style: { color: '#dc2626', fontSize: '13px', marginTop: '4px', display: 'block' } },
                customFieldErrors.terms
              )
            ),
            
            // Submit Button for Custom Fields
            React.createElement(
              'button',
              {
                onClick: handleCustomFieldSubmit,
                style: {
                  width: '100%',
                  padding: '14px',
                  background: '#8B5CF6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                },
                onMouseEnter: (e) => {
                  e.currentTarget.style.background = '#7C3AED';
                },
                onMouseLeave: (e) => {
                  e.currentTarget.style.background = '#8B5CF6';
                }
              },
              React.createElement('i', { className: 'fas fa-shopping-cart', style: { marginRight: '8px' } }),
              'Proceed to Cart'
            )
          ),
          
          // Service Actions - Show for ALL services (including custom, but only when custom fields are NOT shown or already filled)
          // For custom services: Hide the action buttons if custom fields are showing
          // For normal services: Always show the action buttons
          (!isCustom || !showCustomFields) && React.createElement(
            'div',
            { className: 'service-actions', style: { display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '4px' } },
            React.createElement(
              'button',
              { 
                className: `btn-primary ${added ? 'added' : ''}`,
                onClick: handleAddToCart,
                style: {
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '14px 32px',
                  background: added ? '#4CAF50' : (isCustom ? '#8B5CF6' : '#65d2bc'),
                  color: added ? '#fff' : (isCustom ? '#fff' : '#0f172a'),
                  fontWeight: '700',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  fontSize: '16px',
                  minHeight: '52px',
                  flex: '1',
                  boxShadow: added ? '0 4px 16px rgba(76, 175, 80, 0.3)' : `0 4px 16px ${isCustom ? '#8B5CF6' : categoryColor}40`
                }
              },
              added ? 
                React.createElement(React.Fragment, null, React.createElement('i', { className: 'fas fa-check' }), ' Added to Cart') : 
                React.createElement(React.Fragment, null, React.createElement('i', { className: 'fas fa-shopping-cart' }), isCustom ? ' Add to Cart (Custom)' : ' Add to Cart')
            ),
            React.createElement(
              'button',
              { 
                className: 'btn-outline',
                onClick: () => handleNavigation('/cart'),
                style: {
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '14px 32px',
                  background: 'transparent',
                  color: '#1e293b',
                  border: '2px solid #1e293b',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  fontWeight: '600',
                  fontSize: '16px',
                  minHeight: '52px',
                  flex: '1'
                }
              },
              React.createElement('i', { className: 'fas fa-shopping-bag' }),
              ' View Cart'
            ),
            React.createElement(
              'button',
              { 
                className: 'btn-secondary',
                onClick: () => handleNavigation('/'),
                style: {
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '14px 32px',
                  background: '#f1f5f9',
                  color: '#1e293b',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  fontWeight: '600',
                  fontSize: '16px',
                  minHeight: '52px',
                  flex: '1'
                }
              },
              React.createElement('i', { className: 'fas fa-arrow-left' }),
              ' Back to Home'
            )
          )
        )
      )
    )
  );
};

export default ServicePage;